import React from 'react';
import { Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import ImageDropUploader from 'dashboard/components/common/ImageDropUploader';
import RichTextField from 'dashboard/components/fields/RichTextField';

/**
 * One field of a content-library entry, drawn as whatever it actually is.
 *
 * Every field used to be the same single-line box: a blog post and its
 * author's name got identical controls, and a picture was a file drop
 * labelled "Drop your logo here" whatever the entry was.
 *
 * Pictures go through the ordinary uploader rather than the dialog's
 * single-file multipart path. That path could only ever carry one image, so a
 * recommendation with both a photo and a logo had no way to set the second —
 * and it gives a preview of what is already there, which a bare drop target
 * does not.
 */
export default function EntryFormField({ field, value, brandId, onChange }) {
  const set = (next) => onChange({ target: { value: next } });

  if (field.kind === 'image') {
    return (
      <Box>
        <Typography variant='body2' fontWeight='700' mb='6px'>{field.label}</Typography>
        <ImageDropUploader
          brandId={brandId}
          currentSrc={value || ''}
          onUploaded={(url) => set(url)}
        />
        {value && (
          <Box
            component='button'
            type='button'
            onClick={() => set('')}
            sx={{
              mt: '6px', border: 'none', background: 'none', p: 0,
              font: 'inherit', fontSize: 12, color: 'primary.main', cursor: 'pointer',
            }}
          >
            Remove this picture
          </Box>
        )}
      </Box>
    );
  }

  /**
   * A yes/no field, drawn as a switch.
   *
   * There was no branch for these at all, so every one of them fell through
   * to the single-line text box at the bottom of this file: "Published" on a
   * blog post was an empty box you could type anything into, and whatever you
   * typed was stored against a Boolean — which made it `true` whatever it
   * said. The box also looked broken, because a text field with no label
   * inside it and nothing in it is indistinguishable from one that failed.
   */
  if (field.kind === 'boolean') {
    return (
      <Box>
        <FormControlLabel
          control={(
            <Switch
              id={`f-${field.name}`}
              checked={Boolean(value)}
              onChange={(e) => set(e.target.checked)}
            />
          )}
          label={field.label}
          sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontWeight: 700, fontSize: '0.875rem' } }}
        />
        {field.help && (
          <Typography variant='caption' color='text.secondary' display='block'>
            {field.help}
          </Typography>
        )}
      </Box>
    );
  }

  if (field.kind === 'longtext') {
    return (
      <RichTextField
        mb='0px'
        id={`f-${field.name}`}
        label={field.label}
        value={value || ''}
        onChange={onChange}
        placeholder={field.placeholder || `Write the ${field.label.toLowerCase()}…`}
      />
    );
  }

  const type = field.kind === 'date' ? 'date'
    : field.kind === 'email' ? 'email'
      : field.kind === 'tel' ? 'tel'
        : field.kind === 'url' ? 'url'
          : 'text';

  return (
    <Box>
      <Typography variant='body2' fontWeight='700' mb='6px'>{field.label}</Typography>
      <TextField
        id={`f-${field.name}`}
        size='small'
        fullWidth
        type={type}
        // A date arrives from the server as a full ISO timestamp, which the
        // native date input rejects outright — it wants YYYY-MM-DD and shows
        // an empty box for anything else, so a saved date looked unset.
        value={type === 'date' ? String(value || '').slice(0, 10) : (value || '')}
        onChange={onChange}
        placeholder={field.placeholder}
        helperText={field.help}
        // A date input with no value shows its own placeholder, so the label
        // has to stay out of the way rather than sitting on top of it.
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      />
    </Box>
  );
}
